import React, { useEffect, useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MilestoneBadges — supportive, never competitive. Fetches earned badges +
 * the "next up" badge per stat, so the profile always shows both what
 * you've achieved and what's within reach. No leaderboards.
 */
const MilestoneBadges = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('rbr_token');
        const res = await fetch(`${API_URL}/api/user/milestones`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const body = await res.json();
          if (!cancelled) setData(body);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-stone-400 text-sm gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> loading milestones…
      </div>
    );
  }
  if (!data) return null;

  const { earned = [], upcoming = [] } = data;

  return (
    <div className="px-5 mb-6" data-testid="milestone-badges">
      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold text-[#D6AD60] mb-3">
        <Sparkles className="w-3 h-3" /> Milestones
      </p>

      {earned.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {earned.map((b) => (
            <div
              key={b.id}
              className="rounded-2xl p-3 text-center bg-[#FFFCF3] border border-[#D6AD60]/40 shadow-sm"
              data-testid={`milestone-earned-${b.id}`}
              title={b.description}
            >
              <div className="text-2xl mb-1">{b.emoji}</div>
              <p className="text-[10px] font-semibold text-stone-700 leading-tight">{b.title}</p>
            </div>
          ))}
        </div>
      )}

      {upcoming.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.20em] font-semibold text-stone-400 mb-2">Next up</p>
          <div className="space-y-2">
            {upcoming.map((b) => {
              const pct = Math.min(100, Math.round((b.current / b.threshold) * 100));
              return (
                <div
                  key={b.id}
                  className="rounded-2xl p-3 bg-[#FFFCF3] border border-stone-200"
                  data-testid={`milestone-upcoming-${b.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-xl opacity-70 grayscale">{b.emoji}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-stone-700">{b.title}</p>
                      <p className="text-[10px] text-stone-500 leading-tight">{b.description}</p>
                    </div>
                    <span className="text-[11px] font-mono text-stone-400 flex-shrink-0">{b.current}/{b.threshold}</span>
                  </div>
                  <div className="mt-2 h-1 rounded-full bg-stone-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#D6AD60,#AA8347)' }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {earned.length === 0 && upcoming.length === 0 && (
        <p className="text-xs text-stone-400 text-center py-4">
          Your first milestone is waiting — read a reflection or take a breath.
        </p>
      )}
    </div>
  );
};

export default MilestoneBadges;
